import { useMemo } from "react";
import clsx from "clsx";
import type { CurrentUser } from "@/lib/auth";
import { userIsBuero, userIsMonteure } from "@/lib/auth";
import type { TimesheetSummary } from "@/lib/timesheets";
import {
  formatInvoiceValue,
  getContactAgeInfo,
  getContactAgeThresholds,
  getTimesheetContactName,
  getTimesheetContactSearchTerm,
  getTimesheetStatus,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";

export function TimesheetSummaryPanels({
  rows,
  user,
  searchQuery,
  onContactAgingFilter,
}: {
  rows: TimesheetSummary[];
  user: CurrentUser | null;
  searchQuery: string;
  onContactAgingFilter: (term: string) => void;
}) {
  const showSummary = userIsBuero(user);
  const showContactAging = userIsBuero(user) || userIsMonteure(user);

  const summaryData = useMemo(
    () => rows.filter((ts) => getTimesheetStatus(ts) !== "archived"),
    [rows],
  );

  const summaryStats = useMemo(() => {
    const count = summaryData.length;
    const hours = summaryData.reduce((sum, ts) => sum + (Number(ts.total_hours) || 0), 0);
    const avgHours = count > 0 ? hours / count : 0;
    const billingStats = summaryData.reduce(
      (acc, ts) => {
        const status = getTimesheetStatus(ts);
        if (status === "invoice_created" || status === "invoice_paid") {
          acc.count += 1;
          acc.hours += Number(ts.total_hours) || 0;
        }
        return acc;
      },
      { count: 0, hours: 0 },
    );
    const invoiceSum = summaryData.reduce(
      (sum, ts) => sum + (Number(ts.invoice_value) || 0),
      0,
    );
    return { count, hours, avgHours, billingStats, invoiceSum };
  }, [summaryData]);

  const contactAgingItems = useMemo(() => {
    const now = Date.now();
    const grouped = new Map<
      string,
      {
        name: string;
        searchTerm: string;
        count: number;
        maxDays: number;
        maxThreshold: ReturnType<typeof getContactAgeThresholds>[number] | null;
        distribution: Map<string, number>;
      }
    >();

    summaryData.forEach((ts) => {
      const info = getContactAgeInfo(ts, now);
      if (!info) return;
      const { ageDays, threshold } = info;
      const name = getTimesheetContactName(ts);
      const searchTerm = getTimesheetContactSearchTerm(ts) || name;
      const key = name.toLowerCase();
      const entry = grouped.get(key) ?? {
        name,
        searchTerm,
        count: 0,
        maxDays: 0,
        maxThreshold: null,
        distribution: new Map(),
      };
      entry.count += 1;
      entry.maxDays = Math.max(entry.maxDays, ageDays);
      if (!entry.maxThreshold || threshold.days > entry.maxThreshold.days) {
        entry.maxThreshold = threshold;
      }
      entry.distribution.set(
        threshold.label,
        (entry.distribution.get(threshold.label) || 0) + 1,
      );
      grouped.set(key, entry);
    });

    return Array.from(grouped.values()).sort(
      (a, b) => b.maxDays - a.maxDays || b.count - a.count || a.name.localeCompare(b.name, "de"),
    );
  }, [summaryData]);

  if (!showSummary && !showContactAging) return null;

  const fmtHours = (n: number) =>
    n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-3">
      {showSummary ? (
        <div className="grid grid-cols-1 gap-2 rounded-ios-lg bg-fill-quaternary/25 px-3 py-4 ring-1 ring-separator/30 sm:grid-cols-3 lg:grid-cols-6 dark:ring-separator/45 md:px-4">
          <SummaryItem label="Stundennachweise" value={String(summaryStats.count)} />
          <SummaryItem label="Stunden" value={fmtHours(summaryStats.hours)} />
          <SummaryItem label="Ø Stunden/Nachweis" value={fmtHours(summaryStats.avgHours)} />
          <SummaryItem label="Abgerechnete Nachweise" value={String(summaryStats.billingStats.count)} />
          <SummaryItem label="Abgerechnete Stunden" value={fmtHours(summaryStats.billingStats.hours)} />
          <SummaryItem label="Rechnungswert" value={formatInvoiceValue(summaryStats.invoiceSum)} />
        </div>
      ) : null}

      {showContactAging ? (
        <div className="rounded-ios-lg bg-fill-quaternary/20 px-4 py-4 ring-1 ring-separator/30 dark:ring-separator/45">
          <h3 className="text-sm font-semibold text-label">Ansprechpartner &gt; 1 Woche</h3>
          {contactAgingItems.length === 0 ? (
            <p className="mt-2 text-sm text-label-secondary">
              Keine Nachweise überfällig beim Ansprechpartner.
            </p>
          ) : (
            <ul className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,14.5rem),1fr))] gap-2">
              {contactAgingItems.map((entry) => {
                const filterValue = String(entry.searchTerm || entry.name || "").trim();
                const isActive =
                  filterValue &&
                  filterValue.toLowerCase() === searchQuery.trim().toLowerCase();
                const countLabel = entry.count === 1 ? "Nachweis" : "Nachweise";
                const maxLabel = entry.maxThreshold?.label || "> 1 Woche";
                return (
                  <li key={entry.name} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        const current = searchQuery.trim();
                        onContactAgingFilter(
                          filterValue &&
                            filterValue.toLowerCase() === current.toLowerCase()
                            ? ""
                            : filterValue,
                        );
                      }}
                      className={clsx(
                        "flex w-full min-h-[5.25rem] min-w-0 flex-col gap-2 rounded-ios px-3 py-3 text-left text-sm transition ring-1",
                        isActive
                          ? "bg-accent-soft ring-accent/40"
                          : "bg-card ring-separator/40 hover:bg-fill-tertiary",
                      )}
                    >
                      <span className="truncate font-semibold text-label">{entry.name}</span>
                      <span className="text-xs text-label-secondary">
                        {entry.count} {countLabel}, max{" "}
                        {entry.maxThreshold ? (
                          <TimesheetBadge compact className={entry.maxThreshold.className}>
                            {maxLabel}
                          </TimesheetBadge>
                        ) : (
                          maxLabel
                        )}
                      </span>
                      <span className="flex flex-wrap gap-1">
                        {getContactAgeThresholds()
                          .filter((t) => entry.distribution.has(t.label))
                          .map((t) => (
                            <TimesheetBadge key={t.label} compact className={t.className}>
                              {entry.distribution.get(t.label)}× {t.label}
                            </TimesheetBadge>
                          ))}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-[3.25rem] min-w-0 flex-col justify-center">
      <div className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold tabular-nums text-label">{value}</div>
    </div>
  );
}
